import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer,
} from "recharts";
import { supabase } from "../supabase";
import { C, MOIS, mono } from "../config/theme";
import { fmt } from "../utils/format";
import KpiCard from "../components/KpiCard";
import FilterBar from "../components/FilterBar";
import CustomTooltip from "../components/CustomTooltip";
import DrillDown from "../components/DrillDown";
import MiniTable from "../components/MiniTable";
import StatusMessage from "../components/StatusMessage";

/* ─── Palette donut ─── */
const COLORS = [
  "#F59E0B", "#3B82F6", "#16A34A", "#8B5CF6", "#EC4899",
  "#0EA5E9", "#F97316", "#6366F1", "#14B8A6", "#EF4444",
];

/* ─── Labels types de pièces ─── */
const TYPE_LABELS = {
  situation: "Situation",
  avoir: "Avoir",
  dgd: "DGD",
  facture_directe: "Facture directe",
};

/* ═══════════════════════════════════════════════════════════════════════════════
   PAGE CA — Dashboard chiffre d'affaires (18 KPIs + drill-downs)
   MCD v14 — Données chargées via RPCs bv_rpc_ca_*
   ═══════════════════════════════════════════════════════════════════════════════ */
export default function PageCA() {
  /* ─── État filtres ─── */
  const [annee, setAnnee] = useState(null);
  const [moisDebut, setMoisDebut] = useState(1);
  const [moisFin, setMoisFin] = useState(12);
  const [filClient, setFilClient] = useState("");
  const [filChargeAffaires, setFilChargeAffaires] = useState("");
  const [filConducteur, setFilConducteur] = useState("");
  const [filActivite, setFilActivite] = useState("");
  const [filAgence, setFilAgence] = useState("");
  const [drillDown, setDrillDown] = useState(null);

  /* ─── Données filtres (dropdowns) ─── */
  const [filtresData, setFiltresData] = useState(null);

  /* ─── Données KPIs ─── */
  const [caGlobal, setCaGlobal] = useState(null);
  const [caEvolution, setCaEvolution] = useState([]);
  const [caRepartition, setCaRepartition] = useState([]);
  const [caCumuleMarche, setCaCumuleMarche] = useState(null);
  const [topClients, setTopClients] = useState(null);
  const [topChantiers, setTopChantiers] = useState(null);
  const [caChargeAffaires, setCaChargeAffaires] = useState([]);
  const [caConducteur, setCaConducteur] = useState([]);
  const [caActivite, setCaActivite] = useState([]);
  const [caAgence, setCaAgence] = useState([]);

  /* ─── Données drill-down ─── */
  const [detailFactures, setDetailFactures] = useState([]);
  const [detailChantiers, setDetailChantiers] = useState([]);

  /* ─── Loading ─── */
  const [loadingFiltres, setLoadingFiltres] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState(null);

  /* ─── Paramètres RPC (avec période) ─── */
  const rpcParams = useMemo(() => ({
    p_annee: annee,
    p_mois_debut: moisDebut,
    p_mois_fin: moisFin,
    ...(filClient ? { p_client_id: filClient } : {}),
    ...(filChargeAffaires ? { p_charge_affaires_id: filChargeAffaires } : {}),
    ...(filConducteur ? { p_conducteur_id: filConducteur } : {}),
    ...(filActivite ? { p_activite_id: filActivite } : {}),
    ...(filAgence ? { p_agence_id: filAgence } : {}),
  }), [annee, moisDebut, moisFin, filClient, filChargeAffaires, filConducteur, filActivite, filAgence]);

  /* ─── Paramètres sans période (évolution + cumul vs marché) ─── */
  const rpcParamsSansPeriode = useMemo(() => {
    const { p_mois_debut, p_mois_fin, ...rest } = rpcParams;
    return rest;
  }, [rpcParams]);

  /* ═══════════════════════════════════════════════════════════════════════════
     CHARGEMENT INITIAL — Filtres
     ═══════════════════════════════════════════════════════════════════════════ */
  useEffect(() => {
    async function loadFiltres() {
      setLoadingFiltres(true);
      const { data, error: err } = await supabase.rpc("bv_rpc_ca_filtres");
      if (err) {
        console.error("Erreur filtres:", err);
        setError("Impossible de charger les filtres.");
        setLoadingFiltres(false);
        return;
      }
      setFiltresData(data);
      if (data?.annees?.length > 0) setAnnee(data.annees[0]);
      setLoadingFiltres(false);
    }
    loadFiltres();
  }, []);

  /* ═══════════════════════════════════════════════════════════════════════════
     CHARGEMENT DONNÉES — Quand les filtres changent
     ═══════════════════════════════════════════════════════════════════════════ */
  useEffect(() => {
    if (!annee) return;

    async function loadData() {
      setLoadingData(true);
      setDrillDown(null);

      try {
        const [r1, r2, r3, r4, r5, r6, r7, r8, r9, r10, r11, r12] = await Promise.all([
          supabase.rpc("bv_rpc_ca_global", rpcParams),
          supabase.rpc("bv_rpc_ca_evolution", rpcParamsSansPeriode),
          supabase.rpc("bv_rpc_ca_repartition", rpcParams),
          supabase.rpc("bv_rpc_ca_cumule_vs_marche", rpcParamsSansPeriode),
          supabase.rpc("bv_rpc_ca_top_clients", rpcParams),
          supabase.rpc("bv_rpc_ca_top_chantiers", rpcParams),
          supabase.rpc("bv_rpc_ca_par_charge_affaires", rpcParams),
          supabase.rpc("bv_rpc_ca_par_conducteur", rpcParams),
          supabase.rpc("bv_rpc_ca_par_activite", rpcParams),
          supabase.rpc("bv_rpc_ca_par_agence", rpcParams),
          supabase.rpc("bv_rpc_ca_detail_factures", rpcParams),
          supabase.rpc("bv_rpc_ca_detail_chantiers", rpcParams),
        ]);

        if (r1.data) setCaGlobal(r1.data);
        if (r2.data) setCaEvolution(r2.data || []);
        if (r3.data) setCaRepartition(r3.data || []);
        if (r4.data) setCaCumuleMarche(r4.data);
        if (r5.data) setTopClients(r5.data);
        if (r6.data) setTopChantiers(r6.data);
        if (r7.data) setCaChargeAffaires(r7.data || []);
        if (r8.data) setCaConducteur(r8.data || []);
        if (r9.data) setCaActivite(r9.data || []);
        if (r10.data) setCaAgence(r10.data || []);
        if (r11.data) setDetailFactures(r11.data || []);
        if (r12.data) setDetailChantiers(r12.data || []);

        [r1,r2,r3,r4,r5,r6,r7,r8,r9,r10,r11,r12].forEach((r, i) => {
          if (r.error) console.error(`Erreur RPC #${i + 1}:`, r.error);
        });
      } catch (err) {
        console.error("Erreur chargement CA:", err);
        setError("Erreur lors du chargement des données.");
      } finally {
        setLoadingData(false);
      }
    }

    loadData();
  }, [annee, rpcParams, rpcParamsSansPeriode]);

  /* ═══════════════════════════════════════════════════════════════════════════
     DONNÉES DÉRIVÉES
     ═══════════════════════════════════════════════════════════════════════════ */

  /* Bar chart N vs N-1 — compléter les mois manquants */
  const chartMensuel = useMemo(() => {
    const map = new Map();
    (caEvolution || []).forEach(r => map.set(r.mois, r));
    const result = [];
    for (let m = 1; m <= 12; m++) {
      const entry = map.get(m) || { mois: m, ca_n: 0, ca_n1: 0 };
      result.push({ mois: MOIS[m], N: entry.ca_n, N1: entry.ca_n1 });
    }
    return result;
  }, [caEvolution]);

  /* Sparklines */
  const sparklineCA = useMemo(() =>
    chartMensuel.slice(moisDebut - 1, moisFin).map(r => r.N || 0),
    [chartMensuel, moisDebut, moisFin]
  );

  const sparklineCumul = useMemo(() => {
    if (!caCumuleMarche?.cumul_mensuel) return [];
    let cumul = 0;
    return (caCumuleMarche.cumul_mensuel || []).map(r => { cumul += r.ca_mois; return cumul; });
  }, [caCumuleMarche]);

  /* Donut avec libellés */
  const repartitionData = useMemo(() => {
    return (caRepartition || []).map(r => ({
      ...r,
      name: TYPE_LABELS[r.type_piece] || r.type_piece,
      value: r.montant_ht,
      pct: caGlobal?.ca_facture
        ? ((r.montant_ht / caGlobal.ca_facture) * 100).toFixed(1)
        : "0.0",
    }));
  }, [caRepartition, caGlobal]);

  /* Courbe CA cumulé vs marché */
  const chartCumuleMarche = useMemo(() => {
    if (!caCumuleMarche?.cumul_mensuel) return [];
    return (caCumuleMarche.cumul_mensuel || []).map(r => ({
      mois: MOIS[r.mois],
      ca_cumule: r.ca_cumule,
      marche: caCumuleMarche.marche_total,
    }));
  }, [caCumuleMarche]);

  /* Avoirs extraits du détail factures */
  const detailAvoirs = useMemo(() =>
    (detailFactures || []).filter(f => f.type_piece === "avoir"),
    [detailFactures]
  );

  /* Détail chantiers enrichi avec RAF */
  const detailChantiersRaf = useMemo(() =>
    (detailChantiers || []).map(ch => ({
      ...ch,
      raf: ch.marche_ht - ch.facture_total_ht,
    })),
    [detailChantiers]
  );

  /* ─── Drill-down toggle ─── */
  const toggleDrill = useCallback((key) => setDrillDown(prev => prev === key ? null : key), []);

  /* ─── Helpers totaux MiniTable ─── */
  const totalCA = caGlobal?.ca_facture || 0;
  const pctTotal = useCallback((rows) => {
    const somme = rows.reduce((acc, r) => acc + (r.ca_ht || 0), 0);
    return totalCA > 0 ? `${(somme / totalCA * 100).toFixed(1)}%` : "—";
  }, [totalCA]);

  /* ─── Colonnes drill-down ─── */
  const colonnesFactures = useMemo(() => [
    { key: "numero_piece", label: "N° pièce", bold: true },
    { key: "date_piece", label: "Date", render: r => r.date_piece ? new Date(r.date_piece).toLocaleDateString("fr-FR") : "—" },
    { key: "type_piece", label: "Type", render: r => TYPE_LABELS[r.type_piece] || r.type_piece, color: r => r.type_piece === "avoir" ? C.red : C.green },
    { key: "client", label: "Client" },
    { key: "chantier", label: "Chantier" },
    { key: "conducteur", label: "Conducteur" },
    { key: "montant_ht", label: "Montant HT", align: "right", mono: true, bold: true, render: r => fmt(r.montant_ht) },
  ], []);

  const colonnesChantiers = useMemo(() => [
    { key: "code_erp", label: "Code", bold: true },
    { key: "libelle", label: "Chantier" },
    { key: "client", label: "Client" },
    { key: "conducteur", label: "Conducteur" },
    { key: "activite", label: "Activité" },
    { key: "nb_factures", label: "Factures", align: "right", mono: true },
    { key: "facture_periode_ht", label: "CA période", align: "right", mono: true, bold: true, render: r => fmt(r.facture_periode_ht) },
  ], []);

  const colonnesRaf = useMemo(() => [
    { key: "code_erp", label: "Code", bold: true },
    { key: "libelle", label: "Chantier" },
    { key: "client", label: "Client" },
    { key: "statut", label: "Statut" },
    { key: "marche_ht", label: "Marché HT", align: "right", mono: true, render: r => fmt(r.marche_ht) },
    { key: "facture_total_ht", label: "Facturé total", align: "right", mono: true, render: r => fmt(r.facture_total_ht) },
    { key: "raf", label: "RAF", align: "right", mono: true, bold: true, render: r => fmt(r.raf), color: r => r.raf < 0 ? C.red : C.text },
    { key: "avancement_pct", label: "Avancement", align: "right", mono: true, render: r => `${Number(r.avancement_pct || 0).toFixed(1)}%` },
  ], []);

  /* ═══════════════════════════════════════════════════════════════════════════
     RENDU
     ═══════════════════════════════════════════════════════════════════════════ */

  if (loadingFiltres) {
    return <div style={{ padding: "40px 0", textAlign: "center", color: C.slate }}>Chargement…</div>;
  }

  if (error) {
    return <StatusMessage type="error" message={error} action={{ label: "Réessayer", onClick: () => window.location.reload() }} />;
  }

  if (!filtresData?.annees?.length) {
    return <StatusMessage type="empty" message="Aucune donnée disponible." detail="Les données n'ont pas encore été synchronisées depuis l'ERP." />;
  }

  if (!annee) return null;

  return (
    <div>
      {/* ─── En-tête ─── */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.text }}>Chiffre d'affaires</h1>
        <p style={{ margin: "4px 0 0", fontSize: 13, color: C.slate }}>
          Tableau de bord des ventes et de la facturation
        </p>
      </div>

      {/* ─── Barre de filtres ─── */}
      <FilterBar
        annees={filtresData.annees} annee={annee} setAnnee={setAnnee}
        moisDebut={moisDebut} setMoisDebut={setMoisDebut}
        moisFin={moisFin} setMoisFin={setMoisFin}
        clients={filtresData.clients || []} filClient={filClient} setFilClient={setFilClient}
        chargesAffaires={filtresData.charges_affaires || []} filChargeAffaires={filChargeAffaires} setFilChargeAffaires={setFilChargeAffaires}
        conducteurs={filtresData.conducteurs || []} filConducteur={filConducteur} setFilConducteur={setFilConducteur}
        activites={filtresData.activites || []} filActivite={filActivite} setFilActivite={setFilActivite}
        agences={filtresData.agences || []} filAgence={filAgence} setFilAgence={setFilAgence}
      />

      {/* ─── Chargement en cours ─── */}
      {loadingData && (
        <div style={{ textAlign: "center", padding: "12px 0", color: C.slate, fontSize: 13, marginBottom: 8 }}>
          Actualisation des données…
        </div>
      )}

      {/* ═══ LIGNE 1 : CA Facturé, CA Cumulé, RAF ═══ */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 16, marginBottom: 16 }}>
        <KpiCard
          label="CA facturé (HT)" value={caGlobal?.ca_facture || 0}
          color={C.accent} sparkData={sparklineCA}
          onClick={() => toggleDrill("b1")} selected={drillDown === "b1"}
        />
        <KpiCard
          label="CA cumulé exercice" value={caGlobal?.ca_cumule || 0}
          color={C.blue} sparkData={sparklineCumul}
          onClick={() => toggleDrill("b2")} selected={drillDown === "b2"}
        />
        <KpiCard
          label="Reste à facturer" value={caGlobal?.raf_global || 0}
          color="#8B5CF6"
          onClick={() => toggleDrill("b3")} selected={drillDown === "b3"}
        />
      </div>

      {/* Drill-downs ligne 1 */}
      {drillDown === "b1" && (
        <DrillDown
          title={`Détail CA facturé — ${MOIS[moisDebut]} à ${MOIS[moisFin]} ${annee}`}
          columns={colonnesFactures}
          rows={detailFactures}
          color={C.accent}
        />
      )}
      {drillDown === "b2" && (
        <DrillDown
          title={`CA cumulé par chantier — exercice ${annee}`}
          columns={colonnesChantiers}
          rows={detailChantiers || []}
          color={C.blue}
        />
      )}
      {drillDown === "b3" && (
        <DrillDown
          title="Reste à facturer par chantier"
          columns={colonnesRaf}
          rows={detailChantiersRaf}
          color="#8B5CF6"
        />
      )}

      {/* ═══ LIGNE 2 : Avancement, Chantiers, Panier moyen ═══ */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 16, marginBottom: 16 }}>
        <KpiCard
          label="% Avancement" value={caGlobal?.avancement_moyen || 0}
          color={C.green} suffix="%"
          onClick={() => toggleDrill("b4")} selected={drillDown === "b4"}
        />
        <KpiCard
          label="Chantiers facturés" value={caGlobal?.nb_chantiers || 0}
          color="#0EA5E9" isCurrency={false}
          onClick={() => toggleDrill("b5")} selected={drillDown === "b5"}
        />
        <KpiCard
          label="Panier moyen / chantier" value={caGlobal?.panier_moyen || 0}
          color="#F97316"
        />
      </div>

      {/* Drill-downs ligne 2 */}
      {drillDown === "b4" && (
        <DrillDown
          title="Avancement par chantier"
          columns={colonnesRaf}
          rows={[...detailChantiersRaf].sort((a, b) => (b.avancement_pct || 0) - (a.avancement_pct || 0))}
          color={C.green}
        />
      )}
      {drillDown === "b5" && (
        <DrillDown
          title={`Chantiers facturés — ${MOIS[moisDebut]} à ${MOIS[moisFin]} ${annee}`}
          columns={colonnesChantiers}
          rows={detailChantiers || []}
          color="#0EA5E9"
        />
      )}

      {/* ═══ LIGNE 3 : CA moyen/situation, Avoirs ═══ */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 16, marginBottom: 16 }}>
        <KpiCard
          label="CA moyen / situation" value={caGlobal?.ca_moyen_situation || 0}
          color="#6366F1"
        />
        <KpiCard
          label="Nombre d'avoirs" value={caGlobal?.nb_avoirs || 0}
          color={C.red} isCurrency={false}
          onClick={() => toggleDrill("b8")} selected={drillDown === "b8"}
        />
        <KpiCard
          label="Montant avoirs" value={caGlobal?.montant_avoirs || 0}
          color={C.red}
          onClick={() => toggleDrill("b8")} selected={drillDown === "b8"}
        />
      </div>

      {/* Drill-down avoirs */}
      {drillDown === "b8" && (
        <DrillDown
          title={`Détail avoirs — ${MOIS[moisDebut]} à ${MOIS[moisFin]} ${annee}`}
          columns={colonnesFactures}
          rows={detailAvoirs}
          color={C.red}
        />
      )}

      {/* ═══ GRAPHIQUES : Évolution N vs N-1 + Répartition par type ═══ */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 16, marginBottom: 16 }}>
        {/* Évolution mensuelle */}
        <div style={{ background: C.surface, borderRadius: 14, border: `1px solid ${C.border}`, padding: "16px 20px", boxShadow: "0 1px 8px rgba(0,0,0,0.05)" }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: C.accent, marginBottom: 4 }}>
            Évolution du CA (HT)
          </div>
          <div style={{ display: "flex", gap: 16, marginBottom: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10, color: C.slate }}>
              <div style={{ width: 10, height: 10, borderRadius: 2, background: C.accent }} /> CA {annee}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10, color: C.slate }}>
              <div style={{ width: 10, height: 10, borderRadius: 2, background: C.slateLight }} /> CA {annee - 1}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={chartMensuel} barGap={2}>
              <XAxis dataKey="mois" fontSize={10} tick={{ fill: C.slate }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="N" name={`CA ${annee}`} fill={C.accent} radius={[3, 3, 0, 0]} maxBarSize={24} />
              <Bar dataKey="N1" name={`CA ${annee - 1}`} fill={C.slateLight} radius={[3, 3, 0, 0]} maxBarSize={24} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Répartition CA par type (donut) */}
        <div style={{ background: C.surface, borderRadius: 14, border: `1px solid ${C.border}`, padding: "16px 20px", boxShadow: "0 1px 8px rgba(0,0,0,0.05)" }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "#8B5CF6", marginBottom: 8 }}>
            Répartition du CA par type
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ flexShrink: 0 }}>
              <ResponsiveContainer width={140} height={140}>
                <PieChart>
                  <Pie data={repartitionData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={36} outerRadius={60} strokeWidth={2} stroke={C.surface}>
                    {repartitionData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={v => fmt(v)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div style={{ flex: 1, fontSize: 11 }}>
              {repartitionData.map((item, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "3px 0" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{ width: 8, height: 8, borderRadius: 2, background: COLORS[i % COLORS.length], flexShrink: 0 }} />
                    <span style={{ color: C.text }}>{item.name}</span>
                  </div>
                  <span style={{ fontFamily: mono, fontWeight: 600, color: C.slate }}>{item.pct}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ═══ GRAPHIQUE : CA cumulé vs Marché total (NOUVEAU) ═══ */}
      {chartCumuleMarche.length > 0 && (
        <div style={{ background: C.surface, borderRadius: 14, border: `1px solid ${C.border}`, padding: "16px 20px", boxShadow: "0 1px 8px rgba(0,0,0,0.05)", marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: C.blue, marginBottom: 4 }}>
            CA cumulé vs Marché total
          </div>
          <div style={{ display: "flex", gap: 16, marginBottom: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10, color: C.slate }}>
              <div style={{ width: 10, height: 10, borderRadius: 2, background: C.blue }} /> CA cumulé
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10, color: C.slate }}>
              <div style={{ width: 10, height: 10, borderRadius: 2, background: C.red }} /> Marché total ({fmt(caCumuleMarche?.marche_total || 0)})
            </div>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={chartCumuleMarche}>
              <XAxis dataKey="mois" fontSize={10} tick={{ fill: C.slate }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip content={<CustomTooltip />} />
              <Line type="monotone" dataKey="ca_cumule" name="CA cumulé" stroke={C.blue} strokeWidth={2.5} dot={{ r: 4, fill: C.blue }} />
              <Line type="monotone" dataKey="marche" name="Marché total" stroke={C.red} strokeWidth={1.5} strokeDasharray="8 4" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ═══ TABLEAUX : Top 10 clients + Top 10 chantiers ═══ */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 16, marginBottom: 16 }}>
        <MiniTable title="Top 10 clients" color={C.accent} headers={[
          { key: "rank", label: "#", render: (_, i) => i + 1, align: "center", total: () => "" },
          { key: "raison_sociale", label: "Client", bold: true, maxW: 180, total: () => "Total Top 10" },
          { key: "ca_ht", label: "CA (HT)", align: "right", mono: true, bold: true, render: r => fmt(r.ca_ht), total: rows => fmt(rows.reduce((a, r) => a + r.ca_ht, 0)) },
          { key: "nb_chantiers", label: "Chantiers", align: "right", mono: true, total: rows => rows.reduce((a, r) => a + r.nb_chantiers, 0) },
          { key: "pct_ca", label: "% CA", align: "right", mono: true, render: r => `${r.pct_ca}%`, total: rows => pctTotal(rows) },
        ]} rows={topClients?.clients || []} />

        <MiniTable title="Top 10 chantiers facturés" color="#0EA5E9" headers={[
          { key: "rank", label: "#", render: (_, i) => i + 1, align: "center", total: () => "" },
          { key: "libelle", label: "Chantier", bold: true, maxW: 180, total: () => "Total Top 10" },
          { key: "client", label: "Client", maxW: 140, total: () => "" },
          { key: "ca_ht", label: "CA (HT)", align: "right", mono: true, bold: true, render: r => fmt(r.ca_ht), total: rows => fmt(rows.reduce((a, r) => a + r.ca_ht, 0)) },
          { key: "pct_ca", label: "% CA", align: "right", mono: true, render: r => `${r.pct_ca}%`, total: rows => pctTotal(rows) },
        ]} rows={topChantiers?.chantiers || []} />
      </div>

      {/* ═══ TABLEAUX : CA par chargé d'affaires + conducteur ═══ */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 16, marginBottom: 16 }}>
        <MiniTable title="CA par chargé d'affaires" color="#F97316" headers={[
          { key: "rank", label: "#", render: (_, i) => i + 1, align: "center", total: () => "" },
          { key: "charge_affaires", label: "Chargé d'affaires", bold: true, maxW: 200, total: () => "Total" },
          { key: "ca_ht", label: "CA (HT)", align: "right", mono: true, bold: true, render: r => fmt(r.ca_ht), total: rows => fmt(rows.reduce((a, r) => a + r.ca_ht, 0)) },
          { key: "nb_factures", label: "Factures", align: "right", mono: true, total: rows => rows.reduce((a, r) => a + r.nb_factures, 0) },
          { key: "nb_chantiers", label: "Chantiers", align: "right", mono: true, total: rows => rows.reduce((a, r) => a + r.nb_chantiers, 0) },
        ]} rows={caChargeAffaires || []} />

        <MiniTable title="CA par conducteur de travaux" color="#16A34A" headers={[
          { key: "rank", label: "#", render: (_, i) => i + 1, align: "center", total: () => "" },
          { key: "conducteur", label: "Conducteur", bold: true, maxW: 200, total: () => "Total" },
          { key: "ca_ht", label: "CA (HT)", align: "right", mono: true, bold: true, render: r => fmt(r.ca_ht), total: rows => fmt(rows.reduce((a, r) => a + r.ca_ht, 0)) },
          { key: "nb_factures", label: "Factures", align: "right", mono: true, total: rows => rows.reduce((a, r) => a + r.nb_factures, 0) },
          { key: "nb_chantiers", label: "Chantiers", align: "right", mono: true, total: rows => rows.reduce((a, r) => a + r.nb_chantiers, 0) },
        ]} rows={caConducteur || []} />
      </div>

      {/* ═══ TABLEAUX : CA par activité + agence (NOUVEAU) ═══ */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 16, marginBottom: 16 }}>
        <MiniTable title="CA par activité" color="#8B5CF6" headers={[
          { key: "rank", label: "#", render: (_, i) => i + 1, align: "center", total: () => "" },
          { key: "activite", label: "Activité", bold: true, maxW: 200, total: () => "Total" },
          { key: "ca_ht", label: "CA (HT)", align: "right", mono: true, bold: true, render: r => fmt(r.ca_ht), total: rows => fmt(rows.reduce((a, r) => a + r.ca_ht, 0)) },
          { key: "nb_chantiers", label: "Chantiers", align: "right", mono: true, total: rows => rows.reduce((a, r) => a + r.nb_chantiers, 0) },
          { key: "nb_factures", label: "Factures", align: "right", mono: true, total: rows => rows.reduce((a, r) => a + r.nb_factures, 0) },
        ]} rows={caActivite || []} />

        <MiniTable title="CA par agence" color="#14B8A6" headers={[
          { key: "rank", label: "#", render: (_, i) => i + 1, align: "center", total: () => "" },
          { key: "agence", label: "Agence", bold: true, maxW: 200, total: () => "Total" },
          { key: "ca_ht", label: "CA (HT)", align: "right", mono: true, bold: true, render: r => fmt(r.ca_ht), total: rows => fmt(rows.reduce((a, r) => a + r.ca_ht, 0)) },
          { key: "nb_chantiers", label: "Chantiers", align: "right", mono: true, total: rows => rows.reduce((a, r) => a + r.nb_chantiers, 0) },
          { key: "nb_factures", label: "Factures", align: "right", mono: true, total: rows => rows.reduce((a, r) => a + r.nb_factures, 0) },
        ]} rows={caAgence || []} />
      </div>
    </div>
  );
}